import React, { useEffect, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface Company {
  id: string;
  name: string;
  active: boolean;
  createdAt?: string;
  _count?: { branches?: number; users?: number };
}

const Companies: React.FC = () => {
  const { isAdmin } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({ name: '', active: true });

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch {
      toast.error('Falha ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (company?: Company) => {
    if (company) {
      setEditingCompany(company);
      setFormData({ name: company.name, active: company.active });
    } else {
      setEditingCompany(null);
      setFormData({ name: '', active: true });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await api.put(`/companies/${editingCompany.id}`, formData);
        toast.success('Empresa atualizada');
      } else {
        await api.post('/companies', { name: formData.name });
        toast.success('Empresa criada');
      }
      setShowModal(false);
      fetchCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Operação falhou');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Deseja excluir esta empresa?')) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success('Empresa excluída');
      fetchCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Falha ao excluir');
    }
  };

  if (loading) {
    return <div className="spinner-wrap"><div className="spinner" /></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-header__title">Empresas</h1>
        {isAdmin && (
          <button className="btn btn--primary" onClick={() => openModal()}>
            <Plus size={16} /> Nova Empresa
          </button>
        )}
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Vessels</th>
              <th>Usuários</th>
              <th>Status</th>
              {isAdmin && <th style={{ textAlign: 'right' }}>Ações</th>}
            </tr>
          </thead>
          <tbody>
            {companies.map(company => (
              <tr key={company.id}>
                <td style={{ fontWeight: 500 }}>{company.name}</td>
                <td>{company._count?.branches ?? 0}</td>
                <td>{company._count?.users ?? 0}</td>
                <td>
                  <span className={`badge ${company.active ? 'badge--green' : 'badge--red'}`}>
                    {company.active ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
                    {company.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                {isAdmin && (
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button className="btn btn--outline btn--sm" onClick={() => openModal(company)}>
                        <Edit size={13} /> Editar
                      </button>
                      <button className="btn btn--danger btn--sm" onClick={() => handleDelete(company.id)}>
                        <Trash2 size={13} /> Excluir
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {companies.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: '#718096', padding: '32px 16px' }}>
                  Nenhuma empresa encontrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal__header">
              <h2 className="modal__title">{editingCompany ? 'Editar Empresa' : 'Nova Empresa'}</h2>
              <button className="btn btn--outline btn--sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal__body">
                <div className="form-group">
                  <label className="form-label">Nome</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                {editingCompany && (
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select
                      value={formData.active ? 'active' : 'inactive'}
                      onChange={e => setFormData({ ...formData, active: e.target.value === 'active' })}
                      className="form-input"
                    >
                      <option value="active">Ativo</option>
                      <option value="inactive">Inativo</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="modal__footer">
                <button type="button" className="btn btn--outline" onClick={() => setShowModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn--primary">
                  {editingCompany ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
